import { ModalButton } from '@components/modalButton/ModalButton';
import { ActionViewColumn, HardwareKeyboardArrowDown, HardwareKeyboardArrowUp } from 'material-ui/svg-icons';
import * as React from 'react';
import { compose, withHandlers, withProps, withState } from 'recompose';
import Checkbox from 'material-ui/Checkbox';
import useColumnSettings, { defaultColumnSettings } from './_columnSettingsStore';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { withNotifications } from '@components/hoc/withNotifications';
import { postColumnSettings } from './_columnSettingsActions';
import { IconButton, TextField } from 'material-ui';
import { Col, Grid, Row } from 'react-flexbox-grid';
import * as storage from '@inqool/uas-web-core/es/utils/storage';

const toggleColumnVisibility = (columns: any[], column: string): any[] => {
  const targetColumn = columns.find(v => v.label === column);
  if (targetColumn) {
    const updatedTargetColumn = { ...targetColumn, active: !targetColumn.active };
    return columns.map(v => (v.label === updatedTargetColumn.label ? updatedTargetColumn : v));
  }
  return columns;
};

const changeColumnOrder = (columns: any[], column: string, direction: 1 | -1): any[] => {
  const index = columns.findIndex(v => v.label === column);
  const temp = columns[index + direction];
  columns[index + direction] = columns[index];
  columns[index] = temp;
  return columns;
};

const enhance = compose<any, any>(
  withNotifications({ postColumnSettings }),
  withProps(({ agenda }) => {
    const {
      columns,
      updateColumns,
      settingsId,
      serializeColumnSettings,
      setColumnSettingsId,
      deserializeColumnSettings,
      syncColumnSettings
    } = useColumnSettings();
    return {
      storedColumns: columns[agenda],
      updateStoredColumns: updateColumns,
      settingsId,
      serializeColumnSettings,
      setColumnSettingsId,
      deserializeColumnSettings,
      syncColumnSettings
    };
  }),
  withState('columns', 'updateColumns', undefined),
  withHandlers({
    handleToggleColumnVisibility: ({ columns, updateColumns, storedColumns }) => column => {
      const newColumns = toggleColumnVisibility(columns ? columns : storedColumns, column);
      updateColumns(newColumns);
    },
    handleChangeColumnOrder: ({ columns, storedColumns, updateColumns }) => (column, direction) => {
      const newColumns = changeColumnOrder(columns ? columns : storedColumns, column, direction);
      updateColumns(newColumns);
    },
    handleHideAllColumns: ({ columns, storedColumns, updateColumns }) => () => {
      const newColumns = (columns ? columns : storedColumns).map(v => ({ ...v, active: false }));
      updateColumns(newColumns);
    },
    handleShowAllColumns: ({ columns, storedColumns, updateColumns }) => () => {
      const newColumns = (columns ? columns : storedColumns).map(v => ({ ...v, active: true }));
      updateColumns(newColumns);
    },
    handleRevertChanges: ({ storedColumns, updateColumns }) => () => {
      updateColumns(storedColumns);
    },
    handleResetColumns: ({ updateColumns, agenda }) => () => {
      updateColumns(defaultColumnSettings[agenda]);
    },
    handleSaveColumns: ({
      columns,
      storedColumns,
      updateStoredColumns,
      agenda,
      closeItem,
      settingsId,
      serializeColumnSettings,
      postColumnSettingsNotify,
      setColumnSettingsId,
      deserializeColumnSettings,
      syncColumnSettings
    }) => async () => {
      const newColumns = columns ? columns : storedColumns;
      storage.remove(agenda); // must be here because UAS caches grid columns and it breaks things
      updateStoredColumns(
        agenda,
        newColumns.map(v => ({
          ...v,
          width: Number(v.width) < 1 ? 0 : Number(v.width),
          active: Number(v.width) < 1 ? false : v.active
        }))
      );
      const serializedSettings = serializeColumnSettings();
      const { id, settings } = await postColumnSettingsNotify({ id: settingsId, settings: serializedSettings });
      setColumnSettingsId(id);
      const deserializedSettings = deserializeColumnSettings(settings);
      syncColumnSettings(deserializedSettings);
      closeItem();
    }
  })
);

const View: React.FC<any> = ({
  storedColumns,
  columns,
  handleToggleColumnVisibility,
  closeItem,
  handleSaveColumns,
  updateColumns,
  handleChangeColumnOrder,
  handleRevertChanges,
  handleResetColumns,
  handleHideAllColumns,
  handleShowAllColumns
}) => (
  <div style={{ margin: 20 }}>
    <Grid>
      <Row>
        <Col md={2}>
          <p>
            <b>Zobrazit</b>
          </p>
        </Col>
        <Col md={4}>
          <p>
            <b>Název sloupce</b>
          </p>
        </Col>
        <Col md={3}>
          <p>
            <b>Šířka sloupce</b>
          </p>
        </Col>
        <Col md={3}>
          <p>
            <b>Změnit pořadí</b>
          </p>
        </Col>
      </Row>
      {(columns ? columns : storedColumns).map((v, i) => (
        <Row key={i}>
          <Col md={2}>
            <div style={{ marginTop: 13 }}>
              <Checkbox checked={v.active} onClick={() => handleToggleColumnVisibility(v.label)} />
            </div>
          </Col>
          <Col md={4}>
            <p>{v.label}</p>
          </Col>
          <Col md={3}>
            <TextField
              id={`${v.label}-${i}`}
              value={v.width}
              style={{ width: '100%' }}
              type="number"
              onChange={(_, newWidth) => {
                updateColumns(
                  (columns ? columns : storedColumns).map(c => (c.label === v.label ? { ...c, width: newWidth } : c))
                );
              }}
            />
          </Col>
          <Col md={3}>
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 5 }}>
              <IconButton
                style={{ height: 20, width: 25, margin: 0, padding: 0 }}
                onClick={() => handleChangeColumnOrder(v.label, -1)}
                disableTouchRipple={true}
                disabled={i === 0}
              >
                <HardwareKeyboardArrowUp />
              </IconButton>
              <IconButton
                style={{ height: 20, width: 25, margin: 0, padding: 0 }}
                onClick={() => handleChangeColumnOrder(v.label, 1)}
                disableTouchRipple={true}
                disabled={i === (columns ? columns : storedColumns).length - 1}
              >
                <HardwareKeyboardArrowDown />
              </IconButton>
            </div>
          </Col>
        </Row>
      ))}
      <Row style={{ marginTop: 16, marginBottom: 16 }}>
        <Col md={12}>
          <Button label="Skrýt vše" onClick={handleHideAllColumns} />
          <Button label="Zobrazit vše" onClick={handleShowAllColumns} style={{ marginLeft: 16 }} />
          <Button label="Vrátit změny" onClick={handleRevertChanges} style={{ marginLeft: 16 }} />
          <Button label="Resetovat" onClick={handleResetColumns} style={{ marginLeft: 16 }} />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Button label="Uložit a zavřít" primary={true} onClick={handleSaveColumns} />
          <Button label="Zavřít" onClick={closeItem} style={{ marginLeft: 16 }} />
        </Col>
      </Row>
    </Grid>
  </div>
);

const ColumnManagerModal = enhance(View);

export const ColumnManager: React.FC<{ agenda: string }> = ({ agenda }) => {
  return (
    <div style={{ marginLeft: 5 }}>
      <ModalButton
        label="Sloupce"
        icon={<ActionViewColumn />}
        ModalComponent={modalProps => <ColumnManagerModal agenda={agenda} {...modalProps} />}
        dialogStyles={{
          style: { paddingTop: '10vh' },
          contentStyle: {
            paddingTop: 5,
            transform: 'none'
          },
          bodyStyle: { padding: '0', overflowY: 'auto' }
        }}
      />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/modules/columnSettings/ColumnManager.tsx